import { ID } from '../../types/id'

export type ReportEdit = {
  name: string | null
  resultInterpretation: string | null
}
export type ReportStore = {
  reports: {
    name: string
    testSymbol: string
    normScale1: {
      value: string
      label: string
    }
    normScale2: {
      value: string
      label: string
    }
    confidenceInterval: {
      value: string
      label: string
    }
    personalDataVisible: boolean
    testInfoVisible: boolean
    resultsTableVisible: boolean
    resultsChartVisible: boolean
    testProtocolVisible: boolean
    resultInterpretationVisible: boolean
    resultInterpretation: string
  }
}

export type ReportSingle = {
  name: string
  testSymbol: string
  normScale1: string
  normScale2: string
  confidenceInterval: string
  personalDataVisible: boolean
  testInfoVisible: boolean
  resultsTableVisible: boolean
  resultsChartVisible: boolean
  testProtocolVisible: boolean
  resultInterpretationVisible: boolean
  resultInterpretation: string
}
export type ReportAggregate = {
  name: string
  normScale1: string
  normScale2: string
  confidenceInterval: string
  personalDataVisible: boolean
  testsInfoVisible: boolean
  testsResultsVisible: boolean
  areasResultsVisible: boolean
  testsChartVisible: boolean
  areasChartVisible: boolean
  interprofileChartVisible: boolean
  icfReferenceVisible: boolean
  postdiagnosticVisible: boolean
  resultsInterpretationVisible: boolean
  resultsInterpretation: string
}
export type ReportAggregateStore = {
  aggregateReports: {
    name: string
    testSymbol: string
    normScale1: {
      value: string
      label: string
    }
    normScale2: {
      value: string
      label: string
    }
    confidenceInterval: {
      value: string
      label: string
    }
    personalDataVisible: boolean
    testsInfoVisible: boolean
    testsResultsVisible: boolean
    areasResultsVisible: boolean
    testsChartVisible: boolean
    areasChartVisible: boolean
    interprofileChartVisible: boolean
    icfReferenceVisible: boolean
    postdiagnosticVisible: boolean
    resultsInterpretationVisible: boolean
    resultsInterpretation: string
  }
}

export type ReportInquery = {
  name: string
  normScale: string
  confidenceInterval: string
  personalDataVisible: boolean
  respondentsDataVisible: boolean
  resultsRelatedToPopulationVisible: boolean
  resultsRelatedToDisabilityVisible: boolean
  inquiriesProtocolVisible: boolean
  icfReferenceVisible: boolean
  postdiagnosticVisible: boolean
  resultsInterpretationVisible: boolean
  resultsInterpretation: string
}
export type ReportInqueryStore = {
  inqueryReports: {
    name: string
    testSymbol: string
    normScale: {
      value: string
      label: string
    }
    confidenceInterval: {
      value: string
      label: string
    }
    personalDataVisible: boolean
    respondentsDataVisible: boolean
    resultsRelatedToPopulationVisible: boolean
    resultsRelatedToDisabilityVisible: boolean
    inquiriesProtocolVisible: boolean
    icfReferenceVisible: boolean
    postdiagnosticVisible: boolean
    resultsInterpretationVisible: boolean
    resultsInterpretation: string
  }
}

export type Reports = {
  id: ID
  name: string
  userId: ID
  type: 'SINGLE' | 'AGGREGATE' | 'INQUIRIES'
  state: 'DONE' | 'REQUEST' | 'GENERATING' | 'ERROR'
  requestDate: Date
  generationDate: Date
  size: number | null
  errorDescription: string | null
  resultInterpretation: string | null
}

export type PatientReportData = {
  id: ID
  diagnosticianId: ID
  patientId: ID
  note: string
  startTime: Date
  tests: Array<{ code: string }>
}

export type ModalStateProps = {
  open: boolean
  reportId: number | null
  reportName: string | null
  reportErrorDescription: string
}

export const normScales = [
  { value: 'TEN', label: 'teny' },
  { value: 'STEN', label: 'steny' },
  { value: 'PERCENTILE', label: 'centyle' },
  { value: 'IQ', label: 'ilorazowa' },
  { value: 'RECALC', label: 'wyników przeliczonych' },
]

export const tasks = [
  { value: 'anty', label: 'Antysakady - kontrola uwagi. (Gąsienice i ogórki)' },
  { value: 'stroo', label: 'Stroop - kontrola uwagi. (Cyfry)' },
  { value: 'gono', label: 'Go/no-go - kontrola uwagi. (Ryby)' },
  { value: 'licz', label: 'Zakres liczenia - pamięć robocza. (Samochody)' },
  {
    value: 'przel',
    label: 'Przełączanie - elastyczność poznawcza. (Słońce i deszcz)',
  },
  {
    value: 'oper',
    label:
      'Zakres operacyjny - pamięć robocza ("Zapamiętywanie zwierząt", "Zapamiętywanie liter")',
  },
  { value: 'anal', label: 'Analogie - rozumowanie. (Jabłka i gruszki)' },
  {
    value: 'porz',
    label: 'Porządki liniowe - rozumowanie. (Najwięcej i najmniej)',
  },
  {
    value: 'poro',
    label: 'Porównania - szybkość percepcyjna. (Kosze z zakupami)',
  },
  {
    value: 'prze',
    label: 'Przeszukiwanie - szybkość percepcyjna. (Zwierzęta na półkach)',
  },
  { value: 'szyb', label: 'Szybkość spostrzegania - szybkość percepcyjna ("Takie same obrazki")' },
  {
    value: 'szybspost_spe_nw',
    label: 'Zadanie test szybkości spostrzegania Słuchowy',
  },
  { value: 'czyt', label: 'Zadanie zakres czytania' }, // TEST WYŁĄCZONY
  { value: 'rozp', label: 'Rozpoznawanie liter' },
  { value: 'nazy', label: 'Nazywanie liter' },
  { value: 'deko5-6', label: 'Dekodowanie wyrazów_poziom I' }, //TEST WYŁĄCZONY
  { value: 'deko7-10', label: 'Dekodowanie wyrazów_poziom II i III' },
  { value: 'deko11-18', label: 'Dekodowanie wyrazów_poziom IV' },
  {
    value: 'pseudo-wyr-lvl2i3',
    label: 'Dekodowanie pseudowyrazów_poziom II i III',
  },
  { value: 'pseudo-wyr-lvl4', label: 'Dekodowanie pseudowyrazów_poziom IV' },
  { value: 'PowtZd', label: 'Powtarzanie zdań' },
  { value: 'SylabyPseudoWyr', label: 'Analiza sylabowa - pseudosłowa' },
  { value: 'SylabyWyr_spe', label: 'Analiza sylabowa - słowa' },
  { value: 'GloskiWyr1i2', label: 'Analiza fonemowa - słowa' },
  {
    value: 'GloskiPseudoWyr1i2',
    label: 'Analiza fonemowa - pseudosłowa_poziom II',
  },
  {
    value: 'GloskiPseudoWyr3i4',
    label: 'Analiza fonemowa - pseudosłowa_poziom III i IV',
  },
  { value: 'UsuwanieFonemow', label: 'Usuwanie fonemów' },
  { value: 'wieza', label: 'Wieża' },
  { value: 'przyslowia', label: 'Przysłowia' },
  { value: 'pisanie-lvl2', label: 'Pisanie_poziom II' },
  { value: 'pisanie-lvl3i4', label: 'Pisanie_poziom III i IV' },
  { value: 'pisanie-lvl1_spe', label: 'Pisanie_poziom I' },
  { value: 'czyt_zroz-lvl2', label: 'Czytanie ze zrozumieniem_poziom II' },
  {
    value: 'czyt_zroz-lvl3i4',
    label: 'Czytanie ze zrozumieniem_poziom III i IV',
  },
  { value: 'rozpoz1i2i3', label: 'Rozumienie poleceń' },
]
export const statusLabels = {
  REQUEST: 'Oczekuje',
  GENERATING: 'W trakcie',
  DONE: 'Gotowy',
  ERROR: 'Błąd',
}

export const reportType = [
  {
    value: 'aggregateReport',
    label: 'Raport zbiorczy z baterii testów KAPP_A',
  },
  { value: 'singleReport', label: 'Raport z pojedynczego testu' },
  { value: 'inquiriesReport', label: 'Raport z kwestionariuszy' },
]
export const confidenceInterval = [
  { value: 'MEDIUM', label: '90%' },
  { value: 'LOW', label: '80%' },
  { value: 'HIGH', label: '95%' },
]
export const referenceGroups = ['ADHD', 'IntellectualSlight']

export const stateImages = {
  REQUEST: require('../../images/clockIcon.svg'),
  GENERATING: require('../../images/clockIcon.svg'),
  DONE: require('../../images/checkmarkIcon.svg'),
  ERROR: require('../../images/alertIcon.svg'), // TODO FIND APPRIOPRATE ICON
}
